/**
 * React Query hooks for tenant provisioning API endpoints
 * Following CLAUDE.md guidelines: Always use React Query for REST API calls
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosClient from "../utils/fetchAxiosClient";
import type {
  OnboardingData,
  OnboardingRequestResponse,
  OnboardingStatusResponse,
  WorkdayConfig
} from "./tenant.types";

// Query keys for cache management
export const tenantKeys = {
  all: ["tenant"] as const,
  onboardingStatus: (uuid: string, environment: string) =>
    [...tenantKeys.all, "onboarding-status", uuid, environment] as const,
  onboardingRequest: (uuid: string) =>
    [...tenantKeys.all, "onboarding-request", uuid] as const,
  navigationContext: (uuid: string) =>
    [...tenantKeys.all, "navigation-context", uuid] as const
};

// ============================================================================
// Request/Response Types
// ============================================================================

export interface CopyTenantRequest {
  clientName: string;
  workdayConfig: WorkdayConfig;
  uuid: string;
  targetEnvironment?: string;
}

export interface UpdateOnboardingStageRequest {
  uuid: string;
  stage:
    | "WORKDAY_CONFIGURED"
    | "DOMAIN_CONFIGURED"
    | "SSO_CONFIGURED"
    | "IMPL_COMPLETED";
  tenantId?: string;
  onboardingData?: OnboardingData;
}

export interface MarkStageCompleteRequest {
  uuid: string;
  stage: "workday" | "domain" | "sso";
  completedBy: string;
}

export interface ProvisionProductionTenantsRequest {
  uuid: string;
  sourceTenantId: string;
  productionTenantId: string;
  sandboxTenantId: string;
  previewTenantId: string;
}

// ============================================================================
// Query Hooks (GET requests)
// ============================================================================

/**
 * Hook to get onboarding status for a UUID
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useOnboardingStatus(uuid);
 * if (data?.data?.status === 'WORKDAY_CONFIGURED') { ... }
 * ```
 */
export function useOnboardingStatus(
  uuid: string | null | undefined,
  environment = "implementation",
  options?: { enabled?: boolean }
) {
  return useQuery<OnboardingStatusResponse>({
    queryKey: tenantKeys.onboardingStatus(uuid || "", environment),
    queryFn: async () => {
      const response = await axiosClient.get(
        `/tenant-provision/onboarding-status/${uuid}?environment=${environment}`
      );
      return response.data;
    },
    enabled: !!uuid && options?.enabled !== false,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    retry: false
  });
}

/**
 * Hook to get onboarding request details including stage completion status
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useOnboardingRequest(uuid);
 * if (data?.data?.workdayCompletion?.completedAt) { ... }
 * ```
 */
export function useOnboardingRequest(
  uuid: string | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<OnboardingRequestResponse>({
    queryKey: tenantKeys.onboardingRequest(uuid || ""),
    queryFn: async () => {
      const response = await axiosClient.get(`/client-onboarding/${uuid}`);
      return response.data;
    },
    enabled: !!uuid && options?.enabled !== false,
    staleTime: 2 * 60 * 1000,
    retry: false
  });
}

/**
 * Hook to get backend-computed navigation context for onboarding UI.
 * Returns accessible pages, completion state, navigation sequencing, and Rooster URL.
 */
export function useNavigationContext(
  uuid: string | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: tenantKeys.navigationContext(uuid || ""),
    queryFn: async () => {
      const response = await axiosClient.get(
        `/client-onboarding/${uuid}/navigation-context`
      );
      return response.data;
    },
    enabled: !!uuid && options?.enabled !== false,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false
  });
}

// ============================================================================
// Mutation Hooks (POST/PUT/DELETE requests)
// ============================================================================

/**
 * Hook to copy tenant0 configuration
 *
 * @example
 * ```tsx
 * const { mutate: copyTenant, isPending } = useCopyTenant0();
 * copyTenant({ clientName, workdayConfig, uuid });
 * ```
 */
export function useCopyTenant0() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CopyTenantRequest) => {
      const response = await axiosClient.post(
        "/tenant-provision/copy-tenant0",
        data
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate onboarding status queries after successful copy
      queryClient.invalidateQueries({
        queryKey: tenantKeys.onboardingStatus(variables.uuid, "implementation")
      });
    },
    onError: (error) => {
      console.error("Error copying tenant0:", error);
    }
  });
}

/**
 * Hook to update onboarding stage
 *
 * @example
 * ```tsx
 * const { mutate: updateStage, isPending } = useUpdateOnboardingStage(clientId);
 * updateStage({ uuid, stage: 'WORKDAY_CONFIGURED', onboardingData });
 * ```
 */
export function useUpdateOnboardingStage(
  clientId: string,
  environment = "implementation"
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateOnboardingStageRequest) => {
      const response = await axiosClient.put(
        `/tenant-provision/${clientId}/onboarding-stage?environment=${environment}`,
        data
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: tenantKeys.onboardingStatus(variables.uuid, environment)
      });
      queryClient.invalidateQueries({
        queryKey: tenantKeys.onboardingRequest(variables.uuid)
      });
    },
    onError: (error) => {
      console.error("Error updating onboarding stage:", error);
    }
  });
}

/**
 * Hook to mark a stage as complete in the sequenced onboarding workflow
 *
 * @example
 * ```tsx
 * const { mutate: markComplete, isPending } = useMarkStageComplete();
 * markComplete({ uuid, stage: 'workday', completedBy: user.email });
 * ```
 */
export function useMarkStageComplete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      uuid,
      stage,
      completedBy
    }: MarkStageCompleteRequest) => {
      const response = await axiosClient.post(
        `/client-onboarding/${uuid}/stage/${stage}/complete`,
        { completedBy }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate onboarding request to refresh completion status
      queryClient.invalidateQueries({
        queryKey: tenantKeys.onboardingRequest(variables.uuid)
      });
      // Invalidate navigation context to reflect updated stage completion
      queryClient.invalidateQueries({
        queryKey: tenantKeys.navigationContext(variables.uuid)
      });
    },
    onError: (error, variables) => {
      console.error(`Error marking ${variables.stage} stage complete:`, error);
    }
  });
}

/**
 * Hook to provision production, sandbox, and preview tenants
 *
 * @example
 * ```tsx
 * const { mutate: provision, isPending } = useProvisionProductionTenants();
 * provision({ uuid, sourceTenantId, productionTenantId, sandboxTenantId, previewTenantId });
 * ```
 */
export function useProvisionProductionTenants() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProvisionProductionTenantsRequest) => {
      const response = await axiosClient.post(
        "/tenant-provision/provision-production-tenants",
        data
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: tenantKeys.onboardingRequest(variables.uuid)
      });
    },
    onError: (error) => {
      console.error("Error provisioning production tenants:", error);
    }
  });
}
